import {
    PNG_MAGIC_BYTES,
    TRANSPORT_COMPANY_LOGO_RULES as RULES,
} from '../constants/transportCompanyLogo';

export function validateDimensions(width, height, label) {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return `${label}: o arquivo precisa ter dimensões explícitas.`;
    }
    if (width < RULES.minWidth || width > RULES.maxWidth) {
        return `${label}: largura deve estar entre ${RULES.minWidth} e ${RULES.maxWidth} px.`;
    }
    if (height < RULES.minHeight || height > RULES.maxHeight) {
        return `${label}: altura deve estar entre ${RULES.minHeight} e ${RULES.maxHeight} px.`;
    }
    const aspect = width / height;
    if (aspect < RULES.minAspect || aspect > RULES.maxAspect) {
        return `${label}: proporção largura/altura deve estar entre ${RULES.minAspect.toFixed(1)} e ${RULES.maxAspect.toFixed(1)}.`;
    }
    return null;
}

export function validatePair(png, svg) {
    if (!png || !svg) return null;
    if (
        Math.abs(png.w - svg.w) > RULES.maxDelta
        || Math.abs(png.h - svg.h) > RULES.maxDelta
    ) {
        return `PNG e SVG precisam ter a mesma largura e altura (ex.: ${RULES.referenceWidth}×${RULES.referenceHeight}). PNG ${png.w}x${png.h}, SVG ${svg.w}x${svg.h}.`;
    }
    return null;
}

export function isPngFile(file) {
    const name = String(file?.name || '').toLowerCase();
    const type = String(file?.type || '').toLowerCase();
    return type === 'image/png' || name.endsWith('.png');
}

export function isSvgFile(file) {
    const name = String(file?.name || '').toLowerCase();
    const type = String(file?.type || '').toLowerCase();
    return type === 'image/svg+xml' || name.endsWith('.svg');
}

export function validateFileMeta(file, kind) {
    const label = kind === 'svg' ? 'SVG' : 'PNG';
    if (!file) {
        return `${label}: selecione um arquivo.`;
    }
    if (file.size > RULES.maxBytes) {
        return `${label}: o arquivo deve ter no máximo 1 MiB.`;
    }
    if (kind === 'png' && !isPngFile(file)) {
        return 'O campo PNG aceita apenas arquivos PNG.';
    }
    if (kind === 'svg' && !isSvgFile(file)) {
        return 'O campo SVG aceita apenas arquivos SVG.';
    }
    return null;
}

function parseLength(value) {
    if (value == null) return null;
    const raw = String(value).trim();
    if (!raw || raw.endsWith('%')) return null;
    const match = raw.match(/^([0-9]+(?:\.[0-9]+)?)\s*(px)?$/i);
    if (!match) return null;
    const number = Number(match[1]);
    return Number.isFinite(number) && number > 0 ? number : null;
}

function parseViewBox(value) {
    if (!value) return null;
    const parts = String(value).trim().split(/[\s,]+/).map(Number);
    if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) {
        return null;
    }
    const width = parts[2];
    const height = parts[3];
    if (width <= 0 || height <= 0) return null;
    return { w: width, h: height };
}

function getSvgAttribute(tag, name) {
    const pattern = new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)')`, 'i');
    const match = tag.match(pattern);
    return match ? (match[2] ?? match[3]) : null;
}

export function parseSvgDimensions(svgText) {
    const tag = String(svgText || '').match(/<svg\b[^>]*>/i)?.[0];
    if (!tag) return null;

    const width = parseLength(getSvgAttribute(tag, 'width'));
    const height = parseLength(getSvgAttribute(tag, 'height'));
    if (width && height) {
        return { w: width, h: height };
    }

    return parseViewBox(getSvgAttribute(tag, 'viewBox'));
}

export async function hasPngMagicBytes(file) {
    const buffer = await file.slice(0, PNG_MAGIC_BYTES.length).arrayBuffer();
    const bytes = new Uint8Array(buffer);
    return PNG_MAGIC_BYTES.every((value, index) => bytes[index] === value);
}

export function readImageDimensions(file) {
    return new Promise((resolve, reject) => {
        const objectUrl = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(objectUrl);
            resolve({ w: image.naturalWidth, h: image.naturalHeight });
        };
        image.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('PNG: não foi possível ler as dimensões da imagem.'));
        };
        image.src = objectUrl;
    });
}

export async function inspectPngFile(file) {
    const metaError = validateFileMeta(file, 'png');
    if (metaError) return { error: metaError, dimensions: null };

    const isPng = await hasPngMagicBytes(file);
    if (!isPng) {
        return { error: 'O campo PNG aceita apenas arquivos PNG.', dimensions: null };
    }

    try {
        const dimensions = await readImageDimensions(file);
        const dimensionError = validateDimensions(dimensions.w, dimensions.h, 'PNG');
        return { error: dimensionError, dimensions };
    } catch (error) {
        return { error: error.message || 'PNG: não foi possível ler as dimensões da imagem.', dimensions: null };
    }
}

export async function inspectSvgFile(file) {
    const metaError = validateFileMeta(file, 'svg');
    if (metaError) return { error: metaError, dimensions: null };

    const text = await file.text();
    if (!/<svg\b/i.test(text)) {
        return { error: 'O campo SVG aceita apenas arquivos SVG.', dimensions: null };
    }

    const dimensions = parseSvgDimensions(text);
    if (!dimensions) {
        return {
            error: 'SVG: o arquivo precisa ter width/height ou viewBox com dimensões explícitas.',
            dimensions: null,
        };
    }

    return {
        error: validateDimensions(dimensions.w, dimensions.h, 'SVG'),
        dimensions,
    };
}

export function parseCacheControlMaxAgeMs(cacheControl, fallbackMs) {
    const match = String(cacheControl || '').match(/max-age=(\d+)/i);
    if (!match) return fallbackMs;
    return Number(match[1]) * 1000;
}
